/**
 * QuickSetupForm - Step 1 of Quick Create
 *
 * Collects minimal info needed for compliance check:
 * - State (required, determines available counties)
 * - County (required, triggers jurisdiction lookup)
 * - Permit Type (required)
 * - Address (optional, for better jurisdiction matching)
 *
 * Creates a draft job on completion.
 */

import { useState, useEffect } from 'react';
import { Timestamp, doc, collection } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { dualWriteJob } from '@/lib/firestore-helpers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, MapPin, ArrowRight, Info } from 'lucide-react';
import { useSupportedStates, useCountiesByState, usePermitTypes, useLookupJurisdiction } from '@/hooks/useCompliance';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import type { DraftJobData } from '@/pages/QuickCreate';

interface QuickSetupFormProps {
  contractorId: string;
  userId: string;
  onComplete: (data: DraftJobData) => void;
}

export function QuickSetupForm({ contractorId, userId, onComplete }: QuickSetupFormProps) {
  // Form state
  const [selectedState, setSelectedState] = useState('');
  const [county, setCounty] = useState('');
  const [city, setCity] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [permitType, setPermitType] = useState('');

  // Jurisdiction state
  const [jurisdictionId, setJurisdictionId] = useState<number | null>(null);
  const [jurisdictionCode, setJurisdictionCode] = useState<string | null>(null);
  const [jurisdictionName, setJurisdictionName] = useState<string | null>(null);

  // Loading state
  const [isCreating, setIsCreating] = useState(false);

  // Data hooks
  const { data: states = [], isLoading: statesLoading } = useSupportedStates();
  const { data: counties = [], isLoading: countiesLoading } = useCountiesByState(selectedState || null);
  const { data: permitTypes = [], isLoading: permitTypesLoading } = usePermitTypes();
  const lookupJurisdiction = useLookupJurisdiction();

  // Reset county when state changes
  useEffect(() => {
    setCounty('');
    setJurisdictionId(null);
    setJurisdictionCode(null);
    setJurisdictionName(null);
  }, [selectedState]);

  // Auto-lookup jurisdiction when county changes
  useEffect(() => {
    if (!county || !selectedState) {
      setJurisdictionId(null);
      setJurisdictionCode(null);
      setJurisdictionName(null);
      return;
    }

    const lookup = async () => {
      try {
        const result = await lookupJurisdiction.mutateAsync({
          state: selectedState,
          county,
          city: city || undefined,
          zip: zipCode || undefined,
        });

        if (result.success && result.jurisdiction) {
          setJurisdictionId(result.jurisdiction.id);
          setJurisdictionCode(result.jurisdiction.code);
          setJurisdictionName(result.jurisdiction.ahjName);
        }
      } catch (error) {
        console.error('Jurisdiction lookup failed:', error);
      }
    };

    const timeoutId = setTimeout(lookup, 300);
    return () => clearTimeout(timeoutId);
  }, [county, city, zipCode, selectedState]);

  // Get state name for display
  const getStateName = () => {
    const state = states.find((s) => s.code === selectedState);
    return state?.name || selectedState;
  };

  // Get permit type name for display
  const getPermitTypeName = () => {
    const pt = permitTypes.find((p) => p.code === permitType);
    return pt?.name || permitType;
  };

  // Generate job title
  const generateTitle = () => {
    const permitName = getPermitTypeName();
    if (permitName && county) {
      return `${permitName} - ${county} County`;
    }
    if (permitName) {
      return permitName;
    }
    return 'New Permit';
  };

  // Validation
  const canProceed = selectedState && county && permitType;

  // Handle form submission
  const handleSubmit = async () => {
    if (!canProceed || !contractorId) return;

    setIsCreating(true);

    try {
      // Generate a new job ID
      const jobId = doc(collection(db, 'jobs')).id;

      // Build location string
      const locationParts = [streetAddress, city, county, selectedState, zipCode].filter(Boolean);
      const location = locationParts.join(', ');

      // Create draft job data
      const jobData = {
        createdBy: userId,
        title: generateTitle(),
        description: '',
        location: location || `${county} County, ${selectedState}`,
        address: {
          street: streetAddress || '',
          city: city || '',
          county: county,
          state: selectedState,
          zip: zipCode || '',
        },
        jurisdictionId: jurisdictionId,
        jurisdictionCode: jurisdictionCode,
        jurisdictionAhjName: jurisdictionName,
        permitType: permitType,
        status: 'draft', // Draft status - not visible in normal job list
        projectType: 'residential' as const,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdViaQuickCreate: true,
      };

      // Write to Firestore
      await dualWriteJob(contractorId, jobId, jobData);

      // Pass data to next step
      onComplete({
        jobId,
        title: generateTitle(),
        county,
        city: city || undefined,
        streetAddress: streetAddress || undefined,
        zipCode: zipCode || undefined,
        permitType,
        permitTypeName: getPermitTypeName(),
        jurisdictionId,
        jurisdictionCode,
        jurisdictionName,
      });
    } catch (error) {
      console.error('Failed to create draft job:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Introduction */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Setup</CardTitle>
          <CardDescription>
            Tell us the basics and we'll check if your documents are ready for submission.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Location Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* State Selection */}
          <div className="space-y-2">
            <Label htmlFor="state">
              State <span className="text-red-500">*</span>
            </Label>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger>
                <SelectValue
                  placeholder={statesLoading ? 'Loading states...' : 'Select state'}
                />
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {states.map((s) => (
                  <SelectItem key={s.id} value={s.code}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* County Selection */}
          <div className="space-y-2">
            <Label htmlFor="county">
              County <span className="text-red-500">*</span>
            </Label>
            <Select
              value={county}
              onValueChange={setCounty}
              disabled={!selectedState}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    !selectedState
                      ? 'Select a state first'
                      : countiesLoading
                        ? 'Loading counties...'
                        : 'Select county'
                  }
                />
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {counties.map((c) => (
                  <SelectItem key={c.id} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Determines permitting authority and requirements
            </p>
          </div>

          {/* Jurisdiction Detection */}
          {lookupJurisdiction.isPending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Looking up jurisdiction...
            </div>
          )}

          {jurisdictionName && (
            <div className="p-3 rounded-lg bg-muted/50 border">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{jurisdictionName}</span>
                <ConfidenceBadge level="high" source="County lookup" showLabel={false} />
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Auto-detected permitting authority
              </p>
            </div>
          )}

          {/* Optional Address Fields */}
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-3">
              Optional: Add address for more accurate jurisdiction matching
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="city" className="text-xs">
                  City
                </Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Jacksonville"
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="zip" className="text-xs">
                  ZIP Code
                </Label>
                <Input
                  id="zip"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="32205"
                  className="h-9"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <Label htmlFor="street" className="text-xs">
                  Street Address
                </Label>
                <Input
                  id="street"
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  placeholder="123 Main Street"
                  className="h-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Permit Type Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Permit Type</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="permitType">
              What type of permit? <span className="text-red-500">*</span>
            </Label>
            <Select value={permitType} onValueChange={setPermitType}>
              <SelectTrigger>
                <SelectValue
                  placeholder={permitTypesLoading ? 'Loading permit types...' : 'Select permit type'}
                />
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {permitTypes.map((pt) => (
                  <SelectItem key={pt.id} value={pt.code}>
                    {pt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Determines required documents for verification
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Preview & Continue */}
      {canProceed && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{generateTitle()}</p>
                <p className="text-xs text-muted-foreground">
                  {jurisdictionName || `${county} County, ${getStateName()}`}
                </p>
              </div>
              <Button onClick={handleSubmit} disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
